<template>
    <!-- Build Checklist -->
    <div class="container build-checklist">
        <div class="menu-label">
            <div class="menu-title-field">
                <label class="menu-title-label" for="build-name-input">Build name</label>
                <div class="menu-title-control">
                    <input
                        id="build-name-input"
                        ref="buildNameInput"
                        v-model="buildName"
                        type="text"
                        maxlength="80"
                        autocomplete="off"
                        class="menu-title-input"
                        :class="{ 'is-missing': saveState == 'name-required' && !buildName }"
                        placeholder="Build name"
                        aria-label="Build name"
                        @focus="selectAutomaticBuildName"
                        @input="clearNameError()"
                    >
                    <button
                        type="button"
                        class="menu-title-edit icon icon-pencil"
                        aria-label="Edit build name"
                        title="Edit build name"
                        @click="focusBuildName(true)"
                    ></button>
                </div>
            </div>
            <!-- Only the state worth interrupting for. A build that has never been
                 saved is the default, not news, and the Save button already says
                 so; flagging it permanently was just noise. -->
            <span
                v-if="isEditingSavedBuild && hasUnsavedChanges"
                class="build-title-state"
                title="This saved build has changes that have not been written yet."
            >Unsaved</span>
                <h4 v-if="hasSelectedParts" class="h4-indicator"><div v-if="getBuidStatus && getBuidStatus == 'warning'" class="indicator">
                    <span
                        class="icon icon-warning warning-switch"
                        role="button"
                        tabindex="0"
                        v-gixtip="statusTip()"
                        @click.stop="openBuildOverview"
                        @keydown.enter.prevent="openBuildOverview"
                        @keydown.space.prevent="openBuildOverview"
                    ></span>
                </div>
                <div v-if="getBuidStatus && getBuidStatus == 'ok'" class="indicator">
                    <span
                        class="icon icon-check indicate-ok"
                        role="button"
                        tabindex="0"
                        v-gixtip="statusTip()"
                        @click.stop="openBuildOverview"
                        @keydown.enter.prevent="openBuildOverview"
                        @keydown.space.prevent="openBuildOverview"
                    ></span>
                </div>
                <div v-if="getBuidStatus && getBuidStatus == 'sad'" class="indicator">
                    <span
                        class="icon icon-circle-with-cross indicate-sad"
                        role="button"
                        tabindex="0"
                        v-gixtip="statusTip()"
                        @click.stop="openBuildOverview"
                        @keydown.enter.prevent="openBuildOverview"
                        @keydown.space.prevent="openBuildOverview"
                    ></span>
                </div>
            </h4>
        </div>
        <div
            v-on:click="emitTypeChange(option.type)"
            v-for="(option) in menuOptions"
            :key="option.item.id"
            class="menu-option"
            :class="{
                'option-active': $route.name == option.type.toLowerCase(),
                'option-selected': option.item.contents !== null,
                'option-unavailable': hasUnavailableSelection(option.item.contents),
                'option-incompatible': checkPartCompat({compatTable: getStateCompatTable, type: option.name}) == false
            }"
        >
            <div class="label-link">
                <router-link  ref="rtrLink"
                    @click.native="setLayout('simple')"
                    :to='option.url'
                    class="menu-link"
                    :class="{'is-active': option.name.toLowerCase() == $route.name}"
                    is-active="is-active">
                    <span class="menu-part-label">
                        <span :class="{incompatible: checkPartCompat({compatTable: getStateCompatTable, type: option.name}) == false, selected: option.item.contents!==null}" class="b-icon" v-html="iconClasses[option.item.id - 1]"></span>
                        <span>{{option.name == 'CPU' ? 'Processor': option.name}}</span>
                    </span>
                </router-link> 
            </div>
            <div class="build-item-link">
                <button v-on:click.stop style="outline: none;" v-if="option.item.contents!==null" @click="unset(option.item)" class="item-label btn-reset"><span class="icon icon-circle-with-cross"></span></button>
                <router-link @click.native="setLayout('simple')" v-if="option.item.contents==null" :to="option.url" class="item-label not-selected">Not Selected</router-link>
                <div v-if="option.item.contents!==null && (option.type == 'ram' || option.type == 'gpu' || option.type == 'storage' ||option.type == 'graphics')"  
                    class="item-label selected fadeOut"
                    :class="{'current': option.type == $route.name}" >
                    <router-link v-gixtip.truncated="partTooltip(option)" data-gixtip-overflow :class="{incompatible: checkPartCompat({compatTable: getStateCompatTable, type: option.name}) == false}" @click.native.stop="setLayout('advanced')" style=" text-decoration: none;" :to="option.url+'?view=slider&id='+option.item.contents[0].id + '&redir=true'" class="build-part-name">
                        <span v-if="option.type == 'ram' && option.item.contents.length <2 && option.item.contents.length !==0" class="build-part-name__text" data-gixtip-overflow>{{option.item.contents[0].compat.ram_type + ' ' + parseInt(option.item.contents[0].compat.capacity)}}GB {{option.item.contents[0].brand}}</span>
                        <span v-else-if="option.item.contents.length <2 && option.item.contents.length !==0" class="build-part-name__text" data-gixtip-overflow>{{option.item.contents[0].model}}</span>
                        <span v-if="option.item.contents.length >1 && option.type == 'ram'" class="build-part-name__text" data-gixtip-overflow>
                            {{option.item.contents[0].compat['ram_type']}} 
                            {{getItemValue({item: option.item.contents, field: 'capacity'})}}GB 
                            <span style="color: #00bc8c; margin:0px;">({{option.item.contents.length}})</span>
                        </span>
                        <span v-if="option.item.contents.length >1 && option.type == 'storage'" class="build-part-name__text" data-gixtip-overflow>
                            {{getItemValue({item: option.item.contents, field: 'capacity'})}}TB
                            <span style="color: #00bc8c; margin:0px;">({{option.item.contents.length}})</span>
                        </span>
                        <span v-if="option.item.contents.length >1 && (option.type == 'graphics' || option.type == 'gpu')" class="build-part-name__text" data-gixtip-overflow>
                            {{getItemValue({item: option.item.contents, field: 'model'}).toLowerCase().includes('geforce') ? getItemValue({item: option.item.contents, field: 'model'}).substr(8):getItemValue({item: option.item.contents, field: 'model'})}}
                            <span style="color: #00bc8c; margin:0px;">({{option.item.contents.length}})</span>
                        </span>
                    </router-link>
                    <span
                        v-if="hasUnavailableSelection(option.item.contents)"
                        class="build-unavailable-warning"
                        v-gixtip="'This saved part is no longer available on Amazon.'"
                        tabindex="0"
                        role="img"
                        aria-label="This saved part is no longer available on Amazon."
                    ><span class="icon icon-warning" aria-hidden="true"></span></span>
                </div>
                <div v-else-if="option.item.contents!==null"  :class="{'current': option.item.contents.type == $route.name}" class="item-label selected fadeOut">
                    <router-link v-gixtip.truncated="partTooltip(option)" data-gixtip-overflow @click.native.stop="setLayout('advanced')" :class="{incompatible: checkPartCompat({compatTable: getStateCompatTable, type: option.name}) == false}" :to="option.url+'?view=slider&id='+option.item.contents.id + '&redir=true'" class="build-part-name">{{option.item.contents.model}}</router-link>
                    <span
                        v-if="hasUnavailableSelection(option.item.contents)"
                        class="build-unavailable-warning"
                        v-gixtip="'This saved part is no longer available on Amazon.'"
                        tabindex="0"
                        role="img"
                        aria-label="This saved part is no longer available on Amazon."
                    ><span class="icon icon-warning" aria-hidden="true"></span></span>
                </div>
            </div>
        </div>
        <!-- Build metrics -->
        <div class="build-metrics">
            <div class="metric">
                <div class="metric-head">
                    <span class="metric-label"><span class="icon icon-gauge"></span>Performance<span class="icon icon-info-with-circle metric-info" v-gixtip="perfScoreTip()"></span></span>
                    <span class="metric-value perf-value"><template v-if="displayPerformancePercent >= 100">High end</template><template v-else>{{ displayPerformancePercent }}<span class="perf-max">/100</span></template></span>
                </div>
                <div class="metric-track">
                    <div class="metric-fill perf-fill" :style="{ width: displayPerformancePercent + '%' }"></div>
                </div>
            </div>
            <div class="metric">
                <div class="metric-head">
                    <span class="metric-label"><span class="icon icon-flash"></span>Power</span>
                    <span class="metric-value" :style="{ color: powerMetricColor }">{{ powerMetricText }}</span>
                </div>
                <div class="metric-track">
                    <div class="metric-fill" :style="{ width: displayPowerPercent + '%', background: powerMetricColor }"></div>
                </div>
            </div>
            <TotalCost :expanded="true" class="build-total" />
        </div>
        <!-- The name is edited in the header now, so this is purely the outcome
             of pressing Save: in flight, done, or what went wrong. -->
        <div
            v-if="saveState !== 'idle'"
            class="build-save-status"
            :class="'build-save-status--' + saveState"
            role="status"
            aria-live="polite"
        >
            <template v-if="saveState == 'saving'">
                <span class="icon icon-cycle"></span>
                <span>Saving&hellip;</span>
            </template>
            <template v-else-if="saveState == 'saved'">
                <span class="icon icon-check"></span>
                <span>Saved to <router-link :to="{path: '/users/mybuild'}">My Builds</router-link></span>
            </template>
            <template v-else-if="saveState == 'signin'">
                <span class="icon icon-info-with-circle"></span>
                <span>Sign in to save. Your build is kept as it is.</span>
            </template>
            <template v-else-if="saveState == 'name-required'">
                <span class="icon icon-warning"></span>
                <span>
                    Give this build a name first.
                    <button type="button" class="build-name-suggest" @click="useSuggestedName()">
                        Use &ldquo;{{ suggestedBuildName }}&rdquo;
                    </button>
                </span>
            </template>
            <template v-else>
                <span class="icon icon-warning"></span>
                <span>{{ saveErrorText }}</span>
            </template>
        </div>

        <!-- Starting over throws away whatever is on screen, so it asks first
             whenever there is something that has not been written yet. -->
        <div v-if="newBuildPrompt" class="build-new-prompt" role="dialog" aria-labelledby="new-build-title" aria-describedby="new-build-description" :aria-busy="saveState == 'saving' ? 'true' : 'false'" @keydown.esc.stop.prevent="cancelNewBuild()">
            <div class="build-new-prompt__head"><strong id="new-build-title">Start a new build?</strong></div>
            <p id="new-build-description">{{ newBuildPromptText }}</p>
            <div class="build-new-prompt__actions">
                <button ref="saveBeforeNew" type="button" class="btn btn-primary" :disabled="saveState == 'saving'" @click="saveThenStartNew()">{{ saveState == 'saving' ? 'Saving…' : loggedIn == 'true' ? 'Save & start new' : 'Sign in to save' }}</button>
                <button type="button" class="btn build-new-prompt__discard" :disabled="saveState == 'saving'" @click="startNewBuild()">Start without saving</button>
                <button type="button" class="build-new-prompt__cancel" :disabled="saveState == 'saving'" @click="cancelNewBuild()">Keep editing</button>
            </div>
        </div>

        <div class="build-actions">
            <router-link :to="'/users/mybuild'"><button class="gc-button">My Builds</button></router-link>
            <button ref="newBuildButton" @click="requestNewBuild()" class="gc-button" :disabled="saveState == 'saving'">New build</button>
            <!-- Copy is part of the save control rather than a second note below
                 the action row. It remains available even when there are no
                 changes to the saved build. -->
            <div class="build-save-control" :class="{ 'has-copy-action': isEditingSavedBuild && loggedIn == 'true' && buildHasParts }">
                <button
                    :class="{ inactive: !canSubmitSave }"
                    :disabled="!canSubmitSave"
                    :title="saveButtonHint"
                    @click="submitSave()"
                    class="gc-button"
                >{{ saveButtonLabel }}</button>
                <button
                    v-if="isEditingSavedBuild && loggedIn == 'true' && buildHasParts"
                    type="button"
                    class="gc-button build-save-copy"
                    title="Save as a separate build"
                    aria-label="Save as a separate build"
                    @click="submitSave(true)"
                >Copy</button>
            </div>
        </div>
        <p class="build-affiliate-note">
            <span class="icon icon-info-with-circle" aria-hidden="true"></span>
            <span>GixCore may earn a commission from qualifying Amazon purchases.</span>
        </p>
    </div>
</template>

<script>
import { mapGetters }       from 'vuex';
import { mapActions }       from 'vuex';
import { EventBus }         from '../../eventBus'
import TotalCost            from './TotalCost.vue';
import {functions}          from '../../functions'
import gixtip               from '../../directives/tooltip'
import { hasUnavailableParts } from '../../utils/partAvailability'
import { consumePendingBuildSave, setPendingBuildSave } from '../../utils/pendingSave'
import { selectionTooltip } from '../../utils/buildParts'

// The build overview: where compatibility findings are spelled out.
const BUILD_OVERVIEW_ROUTE = '/builder/home'

export default {
    name: "Menu",
    components: {
        TotalCost,
    },
    directives: {
        gixtip,
    },
    data() {
        return {
            collapsed:      true,
            warnings:       [],
            build:          this.getBuild,
            // idle | saving | saved | signin | error
            saveState:      'idle',
            saveErrorText:  '',
            saveStatusTimer: null,
            newBuildPrompt: false,
            routeName:      this.$route.name,
            buildPerformance: 0,
            notesSwitch:    false,
            okMessage:      'All good!',
            status:         'ok',
            metricAnimationMs: 700,
            displayPerformancePercent: 0,
            displayPowerPercent: 0,
            displayPowerWatts: 0,
            displayPowerWattage: null,
            metricAnimationFrame: null,
            metricTargets: {
                performance: null,
                powerPercent: null,
                powerWatts: null,
                powerWattage: null
            },
            saveRequestContext: null,
            iconClasses:    [
                // CPU
                `<svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" class="bi bi-cpu-fill" viewBox="0 0 16 16">
                    <path d="M6.5 6a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z"/>
                    <path d="M5.5.5a.5.5 0 0 0-1 0V2A2.5 2.5 0 0 0 2 4.5H.5a.5.5 0 0 0 0 1H2v1H.5a.5.5 0 0 0 0 1H2v1H.5a.5.5 0 0 0 0 1H2v1H.5a.5.5 0 0 0 0 1H2A2.5 2.5 0 0 0 4.5 14v1.5a.5.5 0 0 0 1 0V14h1v1.5a.5.5 0 0 0 1 0V14h1v1.5a.5.5 0 0 0 1 0V14h1v1.5a.5.5 0 0 0 1 0V14a2.5 2.5 0 0 0 2.5-2.5h1.5a.5.5 0 0 0 0-1H14v-1h1.5a.5.5 0 0 0 0-1H14v-1h1.5a.5.5 0 0 0 0-1H14v-1h1.5a.5.5 0 0 0 0-1H14A2.5 2.5 0 0 0 11.5 2V.5a.5.5 0 0 0-1 0V2h-1V.5a.5.5 0 0 0-1 0V2h-1V.5a.5.5 0 0 0-1 0V2h-1V.5zm1 4.5h3A1.5 1.5 0 0 1 11 6.5v3A1.5 1.5 0 0 1 9.5 11h-3A1.5 1.5 0 0 1 5 9.5v-3A1.5 1.5 0 0 1 6.5 5z"/>
                </svg>`,
                // Motherboard
                `<svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" class="bi bi-motherboard" viewBox="0 0 16 16">
                    <path d="M11.5 2a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5Zm2 0a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5Zm-10 8a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1h-6Zm0 2a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1h-6ZM5 3a1 1 0 0 0-1 1h-.5a.5.5 0 0 0 0 1H4v1h-.5a.5.5 0 0 0 0 1H4a1 1 0 0 0 1 1v.5a.5.5 0 0 0 1 0V8h1v.5a.5.5 0 0 0 1 0V8a1 1 0 0 0 1-1h.5a.5.5 0 0 0 0-1H9V5h.5a.5.5 0 0 0 0-1H9a1 1 0 0 0-1-1v-.5a.5.5 0 0 0-1 0V3H6v-.5a.5.5 0 0 0-1 0V3Zm0 1h3v3H5V4Zm6.5 7a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h2a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-2Z"/>
                    <path d="M1 2a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-2H.5a.5.5 0 0 1-.5-.5v-1A.5.5 0 0 1 .5 9H1V8H.5a.5.5 0 0 1-.5-.5v-1A.5.5 0 0 1 .5 6H1V5H.5a.5.5 0 0 1-.5-.5v-2A.5.5 0 0 1 .5 2H1Zm1 11a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v11Z"/>
                </svg>`,
                // RAM
                `<svg xmlns="http://www.w3.org/2000/svg" width="21" height="21"  class="bi bi-memory" viewBox="0 0 16 16">
                    <path d="M1 3a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h4.586a1 1 0 0 0 .707-.293l.353-.353a.5.5 0 0 1 .708 0l.353.353a1 1 0 0 0 .707.293H15a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H1Zm.5 1h3a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-4a.5.5 0 0 1 .5-.5Zm5 0h3a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-4a.5.5 0 0 1 .5-.5Zm4.5.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-4ZM2 10v2H1v-2h1Zm2 0v2H3v-2h1Zm2 0v2H5v-2h1Zm3 0v2H8v-2h1Zm2 0v2h-1v-2h1Zm2 0v2h-1v-2h1Zm2 0v2h-1v-2h1Z"/>
                </svg>`,
                // Storage
                `<svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" class="bi bi-hdd-fill" viewBox="0 0 16 16">
                    <path d="M0 10a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-1zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1zm2 0a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1zM.91 7.204A2.993 2.993 0 0 1 2 7h12c.384 0 .752.072 1.09.204l-1.867-3.422A1.5 1.5 0 0 0 11.906 3H4.094a1.5 1.5 0 0 0-1.317.782L.91 7.204z"/>
                </svg>`,
                // Graphics
                `<svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" class="bi bi-gpu-card" viewBox="0 0 16 16">
                    <path d="M4 8a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm7.5-1.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z"/>
                    <path d="M0 1.5A.5.5 0 0 1 .5 1h1a.5.5 0 0 1 .5.5V4h13.5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-.5.5H2v2.5a.5.5 0 0 1-1 0V2H.5a.5.5 0 0 1-.5-.5Zm5.5 4a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5ZM9 8a2.5 2.5 0 1 0 5 0 2.5 2.5 0 0 0-5 0Z"/>
                    <path d="M3 12.5h3.5v1a.5.5 0 0 1-.5.5H3.5a.5.5 0 0 1-.5-.5v-1Zm4 1v-1h4v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5Z"/>
                </svg>`,
                // PSU
                `<span style="font-size: 22px; " class="icon icon-flash"></span>`,
                // CASE
                `<svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" class="bi bi-pc" viewBox="0 0 16 16">
                    <path d="M5 0a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V1a1 1 0 0 0-1-1H5Zm.5 14a.5.5 0 1 1 0 1 .5.5 0 0 1 0-1Zm2 0a.5.5 0 1 1 0 1 .5.5 0 0 1 0-1ZM5 1.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5ZM5.5 3h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1 0-1Z"/>
                </svg>`
            ]
        }
    },
    watch: {
        routeName(){
        },
        automaticBuildName: {
            immediate: true,
            handler(){ this.syncAutomaticBuildName() },
        },
        getDraftBuildNameAutomatic(){
            this.syncAutomaticBuildName()
        },
    },
    computed: {
        ...mapGetters([
            'allMenuOptions',
            'getBuildRaw',
            'getUser',
            'getError',
            'getBuildAdded',
            'getBuild',
            'getBuildTDP',
            'getStateCompatTable',
            'loggedIn',
            'getBuidStatus',
            'getWarnings',
            'isEditingSavedBuild',
            'getSavedBuildName',
            'getDraftBuildName',
            'getDraftBuildNameAutomatic',
            'getUserBuilds',
            'hasUnsavedChanges',
            'buildHasParts',
            'canSaveBuild',
            'suggestedBuildName',
        ]),
        canSubmitSave(){
            if(!this.buildHasParts){ return false }
            // Signed out the button stays live: it routes to sign-in rather than
            // looking broken.
            if(this.loggedIn != 'true'){ return true }
            return this.canSaveBuild
        },
        saveButtonLabel(){
            if(this.isEditingSavedBuild && !this.hasUnsavedChanges){
                return 'Saved'
            }
            return 'Save'
        },
        // Only worth asking when something would actually be lost: a saved build
        // with edits, or a build that has never been written at all.
        needsSavePrompt(){
            if(!this.buildHasParts){ return false }
            if(this.isEditingSavedBuild){ return this.hasUnsavedChanges }
            return true
        },
        newBuildPromptText(){
            if(this.isEditingSavedBuild){
                return 'Save your changes before starting over?'
            }
            return 'Save this parts list before starting over?'
        },
        saveButtonHint(){
            if(!this.buildHasParts){
                return 'Select at least one part before saving.'
            }
            if(this.loggedIn != 'true'){
                return 'Sign in to save this build.'
            }
            if(this.isEditingSavedBuild && !this.hasUnsavedChanges){
                return 'No changes to save.'
            }
            return ''
        },
        hasSelectedParts(){
            var options = this.allMenuOptions || {}
            var keys = Object.keys(options)
            for(var i = 0; i < keys.length; i++){
                var item = options[keys[i]] && options[keys[i]].item
                var contents = item ? item.contents : null
                if(Array.isArray(contents) && contents.length > 0){
                    return true
                }
                if(contents !== null && contents !== undefined && !Array.isArray(contents)){
                    return true
                }
            }
            return false
        },
        buildName: {
            get(){ return this.getDraftBuildName },
            set(value){ this.setDraftBuildName(value) },
        },
        nextBuildNumber(){
            var builds = Array.isArray(this.getUserBuilds) ? this.getUserBuilds : []
            var highest = 0
            builds.forEach((build) => {
                var match = String(build && build.name || '').trim().match(/#(\d+)$/)
                if(match){ highest = Math.max(highest, parseInt(match[1]) || 0) }
            })
            return Math.max(builds.length + 1, highest + 1)
        },
        automaticBuildName(){
            var fallback = 'Build #' + this.nextBuildNumber
            var cpu = this.automaticPartName('cpu')
            var gpu = this.automaticPartName('graphics')
            if(!cpu || !gpu){ return fallback }

            var suffix = ' #' + this.nextBuildNumber
            var smartName = cpu + ' + ' + gpu
            var availableLength = 80 - suffix.length
            if(smartName.length > availableLength){
                smartName = smartName.slice(0, Math.max(1, availableLength - 3)).trim() + '...'
            }
            return smartName + suffix
        },
        powerMetricText(){
            if(this.displayPowerWattage){
                return this.displayPowerWatts + ' / ' + this.displayPowerWattage + ' W'
            }
            return this.displayPowerWatts + ' W'
        },
        powerMetricColor(){
            return this.getPowerInfo().color
        },
    },
    methods: {
        openBuildOverview(){
            if(this.$route.path == BUILD_OVERVIEW_ROUTE){ return }
            this.$router.push(BUILD_OVERVIEW_ROUTE).catch(()=>{})
        },
         ...mapActions([
            'updateBuildStatus',
            'rmFromMenu',
            'rmFromBuild',
            'getBuild',
            'getPrice',
            'fetchItems',
            'setCurrentPartType',
            'saveBuild',
            'updateSavedBuild',
            'detachSavedBuild',
            'setDraftBuildName',
            'setAutomaticBuildName',
            'buildReset',
            'compatTableReset',
            'setLayout',
            'itemsLoaded'
        ]),
        createCompatTable:  (build)             =>  functions.createCompatTable(build),
        getPerformance:     (build)             =>  functions.getBuildPerformance(build),
        getItemValue:       ({item, field})     =>  functions.getItemValueMenu({item, field}),
        hasUnavailableSelection: (contents)      =>  hasUnavailableParts(contents),
        partTooltip(option){
            var contents = option && option.item ? option.item.contents : null
            return selectionTooltip(contents)
        },
        automaticPartName(menuKey){
            var option = this.allMenuOptions && this.allMenuOptions[menuKey]
            var contents = option && option.item ? option.item.contents : null
            var item = Array.isArray(contents) ? contents[0] : contents
            return item && item.model ? String(item.model).trim() : ''
        },
        syncAutomaticBuildName(){
            if(this.isEditingSavedBuild || !this.getDraftBuildNameAutomatic){ return }
            if(this.getDraftBuildName !== this.automaticBuildName){
                this.setAutomaticBuildName(this.automaticBuildName)
            }
        },
        selectAutomaticBuildName(event){
            if(!this.getDraftBuildNameAutomatic || !event || !event.target){ return }
            event.target.select()
        },
        setStatus(status){
            this.status = status
        },
        getStatus(){
            return this.status
        },
        // Performance score (functions.getBuildPerformance) rendered as a 0-100%
        // bar. 160 is treated as the top of the scale, matching the old chart.
        getPerformancePercent(){
            var result = this.getPerformance(this.menuOptions)
            var total = result && result.Total ? result.Total : 0
            return Math.min(100, Math.round(total / 160 * 100))
        },
        perfScoreTip(){
            var p = this.getPerformancePercent()
            if(p >= 100){
                return 'High-end build — your parts top our 100-point performance scale, benchmarked against a high-end reference build.'
            }
            return 'Performance score: ' + p + ' of 100 — how your selected parts compare to a high-end reference build.'
        },
        // What the green tick actually means. "All good!" told the builder nothing
        // about what had been verified, so this names the checks that passed.
        compatibilityOkTip(){
            var table = this.getStateCompatTable || {}
            var checks = {}
            var count = 0

            Object.keys(table).forEach((type) => {
                var withTypes = table[type] || {}
                Object.keys(withTypes).forEach((withType) => {
                    var options = withTypes[withType] || {}
                    Object.keys(options).forEach((option) => {
                        if(!options[option]){ return }
                        count++
                        checks[String(option).replace(/[_-]+/g, ' ').toLowerCase()] = true
                    })
                })
            })

            var selected = this.selectedPartCount()
            if(!count){
                if(selected < 2){
                    return 'Nothing to check yet. Compatibility is verified once two parts that have to work together are both selected.'
                }
                return 'No conflicts found between the parts selected so far.'
            }

            var names = Object.keys(checks).sort().slice(0, 6)
            return count + ' compatibility ' + (count === 1 ? 'check has' : 'checks have') + ' passed'
                + ' across your ' + selected + ' selected part' + (selected === 1 ? '' : 's')
                + (names.length ? ': ' + names.join(', ') + '.' : '.')
                + ' Nothing in this build conflicts.'
        },
        selectedPartCount(){
            var options = this.allMenuOptions || {}
            return Object.keys(options).reduce((total, key) => {
                var item = options[key] && options[key].item
                var contents = item ? item.contents : null
                if(Array.isArray(contents)){ return total + (contents.length ? 1 : 0) }
                return total + (contents !== null && contents !== undefined ? 1 : 0)
            }, 0)
        },
        // Tooltip text for the build-status icon.
        statusTip(){
            if(this.getBuidStatus == 'ok'){ return this.compatibilityOkTip() }
            if(this.getWarnings && this.getWarnings.length){ return this.getWarnings.join('\n') }
            if(this.getBuidStatus == 'warning'){ return 'Compatible, with warnings to review. Open the build summary for details.' }
            if(this.getBuidStatus == 'sad'){ return 'Compatibility conflict found. Review the highlighted parts before saving the build.' }
            return 'Build status updates as you select parts.'
        },
        getSelectedPowerWattage(){
            var psu = this.menuOptions && this.menuOptions.psu && this.menuOptions.psu.item.contents
            var wattage = psu && psu.specs && psu.specs.wattage ? parseInt(psu.specs.wattage) : null
            if(isNaN(wattage)){
                return null
            }
            return wattage
        },
        // How occupied the power supply is: watts required vs. the PSU's rated
        // wattage. With no PSU selected we can only show the watts the build needs.
        getPowerInfo(){
            var tdp = this.getBuildTDP || 0
            var wattage = this.getSelectedPowerWattage()
            if(!wattage){
                return { percent: 0, color: '#9198aa', text: tdp + ' W', watts: tdp, wattage: null }
            }
            var ratio = tdp / wattage
            // Reuse the build's 80% safe-load convention: good < 80%, tight 80-100%, over > 100%.
            var color = ratio > 1 ? '#e64759' : ratio > 0.8 ? '#e4d836' : '#00bc8c'
            return {
                percent: Math.min(100, Math.round(ratio * 100)),
                color: color,
                text: tdp + ' / ' + wattage + ' W',
                watts: tdp,
                wattage: wattage
            }
        },
        syncMetricTargets(immediate){
            var powerInfo = this.getPowerInfo()
            var targets = {
                performance: this.getPerformancePercent(),
                powerPercent: powerInfo.percent || 0,
                powerWatts: powerInfo.watts || 0,
                powerWattage: powerInfo.wattage || null
            }

            if(this.metricTargets.performance === targets.performance
            && this.metricTargets.powerPercent === targets.powerPercent
            && this.metricTargets.powerWatts === targets.powerWatts
            && this.metricTargets.powerWattage === targets.powerWattage){
                return
            }

            this.metricTargets = targets
            this.displayPowerWattage = targets.powerWattage

            if(immediate){
                this.setMetricDisplays(targets)
                return
            }

            this.animateMetricDisplays(targets)
        },
        setMetricDisplays(targets){
            this.displayPerformancePercent = targets.performance
            this.displayPowerPercent = targets.powerPercent
            this.displayPowerWatts = targets.powerWatts
        },
        animateMetricDisplays(targets){
            if(this.metricAnimationFrame && typeof window !== 'undefined' && window.cancelAnimationFrame){
                window.cancelAnimationFrame(this.metricAnimationFrame)
            }

            if(typeof window === 'undefined' || !window.requestAnimationFrame){
                this.setMetricDisplays(targets)
                return
            }

            var startPerformance = this.displayPerformancePercent || 0
            var startPowerPercent = this.displayPowerPercent || 0
            var startPowerWatts = this.displayPowerWatts || 0
            var duration = this.metricAnimationMs
            var startTime = null
            var self = this

            function easeOutCubic(progress){
                return 1 - Math.pow(1 - progress, 3)
            }

            function step(timestamp){
                if(startTime === null){
                    startTime = timestamp
                }
                var progress = Math.min(1, (timestamp - startTime) / duration)
                var eased = easeOutCubic(progress)
                self.displayPerformancePercent = Math.round(startPerformance + (targets.performance - startPerformance) * eased)
                self.displayPowerPercent = Math.round(startPowerPercent + (targets.powerPercent - startPowerPercent) * eased)
                self.displayPowerWatts = Math.round(startPowerWatts + (targets.powerWatts - startPowerWatts) * eased)

                if(progress < 1){
                    self.metricAnimationFrame = window.requestAnimationFrame(step)
                    return
                }

                self.metricAnimationFrame = null
                self.setMetricDisplays(targets)
            }

            this.metricAnimationFrame = window.requestAnimationFrame(step)
        },
        checkPartCompat({compatTable, type}){
            var table = compatTable
            if(table && type){
                if(table[type.toLowerCase()]){
                    var fields = Object.keys(table[type.toLowerCase()])
                    if(fields && fields.length >0){
                        for(var i = 0; i < fields.length; i++){
                            if(table[type.toLowerCase()][fields[i]]){
                                var values = Object.keys(table[type.toLowerCase()][fields[i]])
                                for(var j = 0; j < values.length; j++){
                                    if(table[type.toLowerCase()][fields[i]][values[j]]
                                    && table[type.toLowerCase()][fields[i]][values[j]].compatible == false
                                    ){
                                        return false
                                    }
                                }
                            }
                        }
                    }
                }
            }
        },
        // Signed out: keep the click rather than dropping it, and send the user to
        // login with a return path. The build is already in localStorage, so it is
        // exactly as they left it when they come back.
        requestSignIn(){
            setPendingBuildSave()
            this.saveState = 'signin'
            this.$router.push({
                path: '/users/login',
                query: { redirect: this.$route.fullPath }
            }).catch(()=>{})
        },
        // One click, one save. asNew forces a copy instead of updating in place.
        async submitSave(asNew){
            if(this.saveState == 'saving'){ return false }
            if(!this.buildHasParts){ return false }

            if(this.loggedIn != 'true'){
                this.requestSignIn()
                return false
            }
            if(!asNew && !this.canSaveBuild){ return false }

            // The name is the user's to choose -- naming a build for them puts a
            // label in their library they never agreed to. Ask, and offer the
            // processor name as a one-click answer.
            var name = (this.getDraftBuildName || '').trim()
            if(!name){
                this.saveState = 'name-required'
                this.focusBuildName()
                return false
            }

            if(asNew){
                await this.detachSavedBuild({ keepDraft: true })
            }

            var user = this.$store.getters.getUser
            if(typeof user == 'string'){
                try { user = JSON.parse(user) } catch(e) { user = null }
            }
            if(!user || !user.id){
                this.requestSignIn()
                return false
            }

            this.saveState = 'saving'
            this.$store.dispatch('setError', null)
            this.$store.dispatch('setBuildAdded', null)

            var payload = Object.assign({ name: name, author_id: user.id }, this.getBuildRaw)
            var response = this.isEditingSavedBuild
                ? await this.updateSavedBuild(payload)
                : await this.saveBuild(payload)

            var ok = !!response && this.getError === null
            this.saveState = ok ? 'saved' : 'error'
            this.saveErrorText = ok ? '' : this.readableSaveError()
            this.$store.dispatch('setBuildAdded', null)

            if(ok){
                // Clear the confirmation on its own so it does not sit there for
                // the rest of the session.
                if(this.saveStatusTimer){ clearTimeout(this.saveStatusTimer) }
                this.saveStatusTimer = setTimeout(() => {
                    if(this.saveState == 'saved'){ this.saveState = 'idle' }
                }, 6000)
            }
            return ok
        },
        focusBuildName(selectText){
            this.$nextTick(() => {
                var input = this.$refs.buildNameInput || this.$el.querySelector('#build-name-input')
                if(!input){ return }
                input.focus()
                if(selectText || this.getDraftBuildNameAutomatic){ input.select() }
            })
        },
        requestNewBuild(){
            if(this.saveState == 'saving'){ return }
            if(!this.needsSavePrompt){
                this.startNewBuild()
                return
            }
            this.newBuildPrompt = true
            this.$nextTick(() => { if(this.$refs.saveBeforeNew){ this.$refs.saveBeforeNew.focus() } })
        },
        cancelNewBuild(){
            if(this.saveState == 'saving'){ return }
            this.newBuildPrompt = false
            this.$nextTick(() => { if(this.$refs.newBuildButton){ this.$refs.newBuildButton.focus() } })
        },
        async saveThenStartNew(){
            if(this.saveState == 'saving'){ return }
            var saved = await this.submitSave()
            // A failed save (missing name, expired session, rejected part) leaves
            // the prompt up rather than discarding work the user asked to keep.
            if(saved){
                this.startNewBuild()
            }
        },
        startNewBuild(){
            if(this.saveState == 'saving'){ return }
            this.newBuildPrompt = false
            this.saveState = 'idle'
            this.buildReset()
        },
        clearNameError(){
            if(this.saveState == 'name-required'){
                this.saveState = 'idle'
            }
        },
        useSuggestedName(){
            this.setDraftBuildName(this.suggestedBuildName)
            this.saveState = 'idle'
            this.focusBuildName()
        },
        readableSaveError(){
            var error = this.getError
            if(!error){ return 'Could not save this build. Please try again.' }
            if(typeof error == 'string'){ return error }
            return 'Could not save this build. Please try again.'
        },
        async emitTypeChange(newType){
            var routeChanging = this.$route.name !== newType
            if(routeChanging){
                this.itemsLoaded(false)
                this.$router.push(newType).catch(()=>{})
            }
            if(newType == 'gpu'){
                newType = 'graphics'
            }
            EventBus.$emit('emitTypeChange', newType)
            this.setCurrentPartType(newType)
        },
        unset(item) {
            this.rmFromBuild({item: item, id: undefined});
            this.rmFromMenu({item: item, id: undefined});
            item.contents = null;
            this.getPrice() 
        },
        getMenuOptions(){
            this.menuOptions = this.allMenuOptions;
        },
        setNotesSwitch(){
            this.notesSwitch = !this.notesSwitch
        },
        openSavePrompt(context){
            if(typeof window !== 'undefined' && window.innerWidth <= 1200){
                return
            }
            this.handleSaveRequest(context)
        },
        openMobileSavePrompt(context){
            if(typeof window !== 'undefined' && window.innerWidth <= 760){
                this.handleSaveRequest(context)
            }
        },
        // "request-build-save" used to open a prompt and wait. It now just saves,
        // and reports back so the builder handoff flow still knows the outcome.
        async handleSaveRequest(context){
            this.saveRequestContext = context && context.source ? context : null
            var ok = await this.submitSave()
            if(this.saveRequestContext){
                var saveContext = this.saveRequestContext
                this.saveRequestContext = null
                EventBus.$emit(ok ? 'build-save-succeeded' : 'build-save-cancelled', saveContext)
            }
        },
    },
    mounted() {
        EventBus.$on('unset', (item) => {
            if (item !== null){
                this.unset(item)
            }
        });
        this.getMenuOptions()
        this.getPrice()
        this.compatTableReset()
        this.buildPerformance = this.getPerformance(this.allMenuOptions).Total
        this.syncMetricTargets(true)
        if(this.warnings && this.warnings.length >0){     
            this.updateBuildStatus({status: 'warning'})
        }
        EventBus.$on('typeChange', (type) => {
            this.emitTypeChange(type)
        });
        EventBus.$on('request-build-save', this.openSavePrompt)
        EventBus.$on('open-mobile-build-save', this.openMobileSavePrompt)
        // Returning from the login page with a save still pending: finish what
        // they asked for instead of making them press Save again.
        if(this.loggedIn == 'true' && consumePendingBuildSave()){
            this.$nextTick(() => { this.submitSave() })
        }
    },
    created(){
        this.$watch(() => { 
            console.log('watching build')
            return this.getBuild;
        },);       
        this.getMenuOptions();
    },
    updated(){
        this.syncMetricTargets()
        if(this.warnings && this.warnings.length >0){     
            this.updateBuildStatus({status: 'warning'})
        }
    },
    beforeDestroy() {
        EventBus.$off('request-build-save', this.openSavePrompt)
        EventBus.$off('open-mobile-build-save', this.openMobileSavePrompt)
        if(this.metricAnimationFrame && typeof window !== 'undefined' && window.cancelAnimationFrame){
            window.cancelAnimationFrame(this.metricAnimationFrame)
        }
        if(this.saveStatusTimer){
            clearTimeout(this.saveStatusTimer)
        }
    }
}
</script>

<style scoped>
.gc-button {
    width: 110px;
}
.build-widget-block a {
    color: transparent;
    display: block;
}
.build-widget-block {
    cursor: pointer;
    transition: 0.2s;
    position: absolute;
    border: 1px solid #434857;
    height: 40px;
    width: 61px;
    margin: 6px;
    border-radius: 4px;
    box-shadow: 1px 1px 1px #9198aa;
}
.build-widget-block:hover {
    border-color: #1997c6 !important;
}
.build-widget-block.active {
    border-color: #1997c6;
    color: transparent;
    box-shadow: 1px 1px 1px #434857;
}
.build-widget-block.chosen {
    border-color: #00bc8c;
    color: transparent;
    box-shadow: 1px 1px 1px #434857; 
}
.build-widget-block.incompatible {
    border-color: #e64759  !important;
    color: transparent;
    box-shadow: 1px 1px 1px #434857; 
}
@media screen and (max-width: 1200px) {
    .item-label {
        width: 120px !important; 
    }
}
.cpu-icon {
    z-index: 10;
    margin-left: 30px;
    margin-top: 25px;
    width: 24px;
    height: 24px;
}
.mb-icon {
    margin-top: 16px;
    margin-left: 16px;
    height: 90px;
    width: 100px;
    border-radius: 6px;
}
.ram-icon-div {
    cursor: pointer;
    position: absolute;
    margin-left: 80px;
    margin-top: 20px;
}
.ram-icon-div:hover .ram-icon-2{
    border-color: #1997c6;
}
.ram-icon-div:hover .ram-icon{
    border-color: #1997c6;
}
.ram-icon {
    width: 8px;
    height: 32px;
}
.ram-icon-2 {
    width: 8px;
    height: 32px;
}
.storage-icon-hdd {
    margin-left: 80px;
    margin-top: 75px;
    width: 30px;
    height: 8px;
}
.storage-icon-m2 {
    margin-left: 48px;
    margin-top: 58px;
    width: 24px;
    height: 8px;
}
.gpu-icon {
    margin-left: 22px;
    margin-top: 80px;
    width: 46px;
    height: 12px;
}
.label-link {
    display: inline-block;
}
.build-item-link {
    display: inline-block;
    float: right;
}
.incompatible {
    color: #E64759 !important;
    fill: #E64759 !important;
}
.dollar-sign {
    float: right;
    font-size: 15px;
}
.price-tag {
    font-size: 24px;
    margin-top: -5px ;
    margin-left: 3px;
}
li {
    list-style-type:none;
    display: inline;
}
.is-active li{
    /* color:  #1997c6 !important; */
}
.is-active .b-icon{
    fill:  #1997c6;
}
.container {
    margin-left: -21px !important;
    height: 100% !important;
    overflow: hidden;
    margin-top: 8px;
    border-right: 1px solid #9198aa;
    padding-left: 18px !important;
    color: rgb(178, 179, 180) !important;
}
.menu-label {
    border-bottom: 1px solid #9198aa;
    margin-bottom: 10px;
}
.menu-label h4{
    font-size: 15pt;
    font-weight: normal;
    letter-spacing: 1px;
    color: #9198aa;
    text-transform: uppercase;
}
.menu-link {
    margin-top: 2px !important;
    color: #9198aa; 
    font-size: 12pt;
    font-weight: lighter;
}
.menu-link:hover {
    text-decoration: unset;
    color: unset;
}
.menu-option{
    font-size: 14pt;
    border: 1px solid #434857;
    padding: 5px;
    margin-bottom: 10px;
    border-radius: 6px;
    padding-left: 7px;
    position: relative;
    transition: 0.2s;
    cursor: pointer;
}
.menu-component .menu-option:hover {
    border: 1px solid #9198aa; 
}
.current {
    color: #1997c6 !important; ;
}
.item-label{
    text-align: right;
    background: transparent;
    position: inherit;
    float: right;
    animation: fadein 1s;
    -moz-animation: fade 1s ; /* Firefox */
    -webkit-animation: fadein 0.4s; /* Safari and Chrome */
    -o-animation: fadein 1s; /* Opera */
    color:#cfd2da;
    max-widtH: 160px;
    width: 9.5vw;
}
.item-label.selected a{
    overflow: hidden;
    vertical-align: inherit;
}
.item-label.selected .b-icon{
    fill: #00bc8c !important;
}
.item-label.selected {
    vertical-align:sub;
    margin-right: 16px;
    box-shadow: 0px 0px 4px #191b20;
    margin-top: -1px;
    /* letter-spacing: 0.4px; */
    font-size: 12pt;
    display: inline-block;
    animation: fadein 1s;
    -moz-animation: fade 1s ; /* Firefox */
    -webkit-animation: fadein 1s; /* Safari and Chrome */
    -o-animation: fadein 1s; /* Opera */
}
 .item-label.selected:hover a{
    text-decoration: none !important;
}
.item-label.not-selected{
    color: #434857;
    font-size: 12pt !important;
    
    margin-right: 4px;
}
.btn-reset {
    position: absolute;
    right: 5px;
    bottom: unset;
    left: unset;
    max-width: 15px;
    display: inline-block;
    background: transparent;
    border: none;
    font-weight: unset;
    font-size: 10pt;
    padding-top: 7px;
    color: #434857;
    margin-right: 4px;
}
.btn-reset:hover {
    cursor: pointer;
    color: #E6474B;
    transform: scale(1.1) !important;
    transition: 0.2s;
}
.unset {
    visibility: hidden;
}
text .current-value {
    font-weight: lighter !important;
}
.subtotal.label {
    color: rgb(178, 179, 180) !important;
    display: inline;
}
.subtotal.price {
    color: #e4d836 !important;
    opacity: 0.9;
    padding-bottom: 0px;
    margin-bottom: 0px;
}
.performance-label{
    text-align: center;
}
.drop-down {transition: 3ms;
    margin-top: 15px;
    padding: 8px;
    padding-bottom: 0px;
    border: 1px solid #1997c6;
    border-bottom: none;
    border-top-right-radius: 6px;
    border-top-left-radius: 6px;
}
.drop-down input {
    width: 90%;
    outline: none;
    padding: 3px;
    margin-bottom: 5px;
    text-align: center;
}
.drop-down-opened {
    transition: 3ms;
    margin-top: 0px !important;
    border-top: none;
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
}
.inactive{
    color: #9198aa !important;
}
.build-widget {
    height: 40px;
    border-radius: 6px;
    position: relative;
}
.b-icon {
    width: 21px;
    height: 21px;
    margin-right: 5px;
    margin-bottom: -4px;
    fill: #9198aa;
}
.b-icon.selected {
    fill: #00bc8c;
    color: #00bc8c;
}
.option-active {
    border-color: #1997c6 !important
}

.build-checklist {
    position: relative;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-left: 0 !important;
    margin-top: 8px;
    padding: 0 26px 10px 18px !important;
    border-right: 0;
    color: #cfd2da !important;
    overflow: visible;
    height: 94% !important;
}

.build-checklist::before {
    position: absolute;
    top: 39px;
    right: 26px;
    left: 18px;
    height: 1px;
    background: linear-gradient(90deg, rgba(25, 151, 198, 0), rgba(25, 151, 198, 0.82), rgba(145, 152, 170, 0.4));
    content: "";
}

.build-checklist::after {
    position: absolute;
    top: -10px;
    right: 0;
    bottom: 0;
    width: 1px;
    background:
        linear-gradient(
            180deg,
            rgba(145, 152, 170, 0),
            rgba(145, 152, 170, 0.26) 12%,
            rgba(25, 151, 198, 0.2) 46%,
            rgba(145, 152, 170, 0.16) 82%,
            rgba(145, 152, 170, 0)
        );
    box-shadow:
        -1px 0 16px rgba(25, 151, 198, 0.08),
        1px 0 18px rgba(0, 0, 0, 0.24);
    content: "";
    pointer-events: none;
}

.build-checklist .menu-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 40px;
    margin: 0 0 0.7rem;
    border-bottom: 0;
}

.build-checklist .menu-label h4 {
    margin: 0;
    color: #cfd2da;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: 0.8px;
    line-height: 1;
    text-transform: uppercase;
}

.build-checklist .menu-title {
    cursor: default;
}

.build-checklist .h4-indicator {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 34px;
}

.indicator {
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.indicator .icon {
    cursor: pointer;
    font-size: 20px;
    opacity: 0.9;
    transition: color 160ms ease, opacity 160ms ease, transform 160ms ease;
}

.indicator .icon:hover,
.indicator .icon.active {
    opacity: 1;
    transform: translateY(-1px);
}

.indicate-ok {
    color: #45b028;
    text-shadow: 0 0 14px rgba(69, 176, 40, 0.5);
}

.warning-switch,
.indicate-warning {
    color: #cfb027;
    text-shadow: 0 0 14px rgba(207, 176, 39, 0.5);
}

.indicate-sad {
    color: #e64759;
    text-shadow: 0 0 14px rgba(230, 71, 89, 0.5);
}

.warnings-screen {
    position: absolute;
    top: 46px;
    right: 26px;
    left: 18px;
    z-index: 5;
    padding: 0.75rem;
    border: 1px solid rgba(67, 72, 87, 0.92);
    border-radius: 6px;
    background:
        linear-gradient(180deg, rgba(25, 27, 32, 0.98), rgba(8, 10, 15, 0.98)),
        rgba(25, 27, 32, 0.98);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);
}

.warning,
.ok-message-item {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    color: #d8dae1;
    font-size: 13px;
    line-height: 1.35;
    text-align: left;
}

.warning + .warning {
    margin-top: 0.5rem;
}

.warning .icon {
    color: #cfb027;
    line-height: 1.35;
}

.ok-message-item {
    color: #bcefdc;
}

.build-checklist .menu-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.7rem;
    min-height: 42px;
    margin-bottom: 0.55rem;
    padding: 0 0.55rem;
    border: 1px solid rgba(67, 72, 87, 0.82);
    border-radius: 6px;
    background: rgba(8, 10, 15, 0.36);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.015);
    cursor: pointer;
    transition: background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}

.build-checklist .menu-option:hover {
    border-color: rgba(25, 151, 198, 0.58);
    background: rgba(25, 151, 198, 0.08);
    box-shadow:
        0 0 18px rgba(25, 151, 198, 0.08),
        inset 0 0 0 1px rgba(255, 255, 255, 0.02);
}

.build-checklist .menu-option.option-active {
    border-color: #1997c6 !important;
    background: rgba(25, 151, 198, 0.1);
    box-shadow:
        0 0 22px rgba(25, 151, 198, 0.12),
        inset 3px 0 0 #1997c6;
}

.build-checklist .menu-option.option-selected {
    border-color: rgba(25, 151, 198, 0.62);
    background: rgba(25, 151, 198, 0.055);
}

.build-checklist .menu-option.option-active.option-selected {
    border-color: #1997c6 !important;
    background: linear-gradient(90deg, rgba(25, 151, 198, 0.14), rgba(25, 151, 198, 0.06));
    box-shadow:
        0 0 22px rgba(25, 151, 198, 0.12),
        inset 3px 0 0 #1997c6,
        inset 0 0 0 1px rgba(255, 255, 255, 0.02);
}

.build-checklist .menu-option.option-incompatible {
    border-color: rgba(230, 71, 89, 0.82) !important;
    background: rgba(120, 20, 28, 0.16);
    box-shadow: inset 3px 0 0 #e64759;
}

.label-link,
.build-item-link {
    min-width: 0;
}

.label-link {
    display: flex;
    align-items: center;
    align-self: stretch;
    flex: 0 0 auto;
}

.build-item-link {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 auto;
    gap: 0.35rem;
    align-self: stretch;
    float: none;
    min-width: 0;
    overflow: hidden;
}

.build-checklist .menu-link {
    display: inline-flex;
    align-items: center;
    height: 100%;
    max-width: 100%;
    margin-top: 0 !important;
    color: #aeb4c3;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 0;
    line-height: 24px;
    text-decoration: none;
}

.build-checklist .menu-link:hover,
.build-checklist .menu-link:focus {
    color: #f4f8fb;
    text-decoration: none;
}

.menu-part-label {
    display: inline-flex;
    align-items: center;
    height: 100%;
    min-width: 0;
}

.build-checklist .b-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 26px;
    height: 26px;
    margin: 0 0.48rem 0 0;
    color: #9198aa;
    fill: currentColor;
    transition: color 160ms ease, fill 160ms ease, transform 160ms ease;
}

.build-checklist .b-icon >>> svg,
.build-checklist .b-icon >>> .icon {
    display: block;
    width: 20px;
    height: 20px;
    margin: 0;
    line-height: 20px;
}

.build-checklist .menu-option:hover .b-icon,
.build-checklist .menu-option.option-active .b-icon {
    color: #1997c6;
    fill: currentColor;
}

.build-checklist .b-icon.selected {
    color: #00bc8c;
    fill: currentColor;
}

.build-checklist .b-icon.incompatible {
    color: #e64759 !important;
    fill: currentColor !important;
}

.build-checklist .item-label {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    align-self: center;
    float: none;
    width: auto;
    max-width: 174px;
    min-width: 0;
    margin: 0;
    color: #cfd2da;
    font-size: 14px !important;
    line-height: 22px;
    text-align: right;
}

.build-checklist .item-label a,
.build-checklist .item-label span {
    overflow: hidden;
    max-width: 100%;
    color: inherit;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.build-checklist .build-part-name {
    display: block;
    min-width: 0;
}

.build-checklist .build-part-name__text {
    display: block;
}

.build-checklist .item-label.selected {
    order: 1;
    flex: 1 1 auto;
    width: auto !important;
    max-width: min(174px, calc(100% - 28px));
    margin: 0;
    padding: 0.17rem 0;
    border-color: transparent !important;
    color: #e5e8ef;
    box-shadow: none;
}

.build-checklist .item-label.selected a:hover,
.build-checklist .item-label.selected a:focus {
    color: #ffffff;
    text-decoration: none !important;
}

.build-checklist .item-label .build-unavailable-warning {
    display: inline-flex;
    flex: 0 0 20px;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    max-width: none;
    margin-left: 6px;
    padding: 0;
    overflow: visible;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: #e0c07e;
    line-height: 1;
    cursor: help;
}

.build-checklist .item-label .build-unavailable-warning:focus-visible {
    outline: 2px solid #e0c07e;
    outline-offset: 2px;
}

.build-checklist .item-label .build-unavailable-warning .icon {
    margin: 0 !important;
    overflow: visible;
    font-size: 14px;
}

.build-checklist .item-label.not-selected {
    color: #646b7c;
    font-size: 14px !important;
}

.build-checklist .item-label.not-selected:hover,
.build-checklist .item-label.not-selected:focus {
    color: #1997c6;
    text-decoration: none;
}

.build-checklist .btn-reset {
    position: static;
    order: 2;
    flex: 0 0 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    max-width: none;
    margin: 0;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: rgba(8, 10, 15, 0.62);
    color: #687084;
    transform: none;
    transition: background 160ms ease, color 160ms ease, transform 160ms ease;
}

.build-checklist .btn-reset:hover,
.build-checklist .btn-reset:focus {
    background: rgba(230, 71, 89, 0.14);
    color: #e64759;
    transform: scale(1.05) !important;
}

.build-checklist .build-metrics {
    display: flex;
    flex-direction: column;
    gap: 0.95rem;
    /* auto top margin pushes the metrics + everything below toward the bottom */
    margin-top: auto;
    padding: 0 0.15rem;
}

.build-checklist .metric {
    width: 100%;
}

.build-checklist .metric-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.42rem;
}

.build-checklist .metric-label {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: #9198aa;
    font-size: 13px;
    letter-spacing: 0.3px;
}

.build-checklist .metric-label .icon {
    font-size: 14px;
    line-height: 1;
    opacity: 0.85;
}

.build-checklist .metric-value {
    color: #cfd2da;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
}

.build-checklist .metric-value.perf-value {
    color: #1997c6;
}

.build-checklist .perf-max {
    font-size: 10px;
    font-weight: 600;
    opacity: 0.6;
}

.build-checklist .metric-info {
    margin-left: 1px;
    color: #6b7280;
    font-size: 11px;
    font-weight: 400;
    cursor: pointer;
    vertical-align: middle;
    transition: color 0.15s ease;
}

.build-checklist .metric-info:hover {
    color: #9198aa;
}

.build-checklist .metric-track {
    width: 100%;
    height: 6px;
    border-radius: 999px;
    background: rgba(67, 72, 87, 0.55);
    overflow: hidden;
}

.build-checklist .metric-fill {
    height: 100%;
    border-radius: 999px;
    transition: background 200ms ease;
}

.build-checklist .perf-fill {
    background: #1997c6;
}

.build-checklist .drop-down {
    margin-top: 0.75rem;
    padding: 0.75rem;
    border: 1px solid rgba(25, 151, 198, 0.72);
    border-radius: 6px 6px 0 0;
    background: rgba(8, 10, 15, 0.72);
    color: #cfd2da;
    font-size: 13px;
    text-align: center;
}

.build-checklist .drop-down input {
    width: 100%;
    min-height: 36px;
    margin-top: 0.35rem;
    margin-bottom: 0;
    padding: 0.45rem 0.6rem;
    border: 1px solid rgba(67, 72, 87, 0.96);
    border-radius: 6px;
    outline: none;
    background: rgba(6, 8, 12, 0.76);
    color: #e5e8ef;
    text-align: center;
}

.build-checklist .drop-down input:focus {
    border-color: #1997c6;
    box-shadow: 0 0 0 3px rgba(25, 151, 198, 0.11);
}

.build-checklist .build-name-field label {
    display: block;
    text-align: center;
}

.build-checklist .build-name-field label span,
.build-checklist .build-name-required {
    color: #e0b85f;
}

.build-checklist .build-name-required {
    display: block;
    margin-top: 0.4rem;
    font-size: 11px;
    line-height: 1.3;
}

.build-checklist .menu-title-field {
    min-width: 0;
    flex: 1 1 auto;
}

.build-checklist .menu-title-label {
    display: block;
    margin: 0 0 0.18rem 0.3rem;
    color: #6f7b90;
    font-size: 8px;
    font-weight: 800;
    letter-spacing: 0.1em;
    line-height: 1;
    text-transform: uppercase;
}

.build-checklist .menu-title-control {
    position: relative;
    min-width: 0;
}

/* The name remains title-like, while the persistent pencil makes its editability
   obvious before the user happens to hover it. */
.build-checklist .menu-title-input {
    width: 100%;
    min-width: 0;
    margin: 0;
    padding: 0.12rem 1.9rem 0.12rem 0.3rem;
    border: 1px solid transparent;
    border-radius: 5px;
    background: transparent;
    color: #cfd2da;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: 0.8px;
    line-height: 1.15;
    text-overflow: ellipsis;
    text-transform: uppercase;
}

.build-checklist .menu-title-input::placeholder {
    color: #9198aa;
    text-transform: uppercase;
}

.build-checklist .menu-title-input:hover {
    border-color: rgba(67, 72, 87, 0.85);
}

.build-checklist .menu-title-input:focus {
    border-color: #1997c6;
    background: rgba(8, 11, 17, 0.7);
    color: #eaf1f8;
    outline: none;
}

.build-checklist .menu-title-edit {
    position: absolute;
    top: 50%;
    right: 0.25rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 25px;
    height: 25px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: #738096;
    cursor: pointer;
    font-size: 13px;
    transform: translateY(-50%);
    transition: color 0.16s ease, background 0.16s ease;
}

.build-checklist .menu-title-control:hover .menu-title-edit,
.build-checklist .menu-title-control:focus-within .menu-title-edit,
.build-checklist .menu-title-edit:hover,
.build-checklist .menu-title-edit:focus-visible {
    background: rgba(25, 151, 198, 0.1);
    color: #46c5ee;
    outline: none;
}

/* Empty name after pressing Save. */
.build-checklist .menu-title-input.is-missing {
    border-color: #e0b85f;
    background: rgba(224, 184, 95, 0.08);
}

/* The heading row carries the divider now, since the state chip below it is out
   of flow and can no longer hold one. */
.build-checklist .menu-label {
    gap: 0.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(67, 72, 87, 0.55);
}


.build-checklist .build-name-suggest {
    display: inline;
    padding: 0;
    border: 0;
    background: transparent;
    color: #77d8f5;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;
}

/* Sits in the heading row beside the status tick, where build metadata already
   lives. Floating it under the divider left it colliding with the first part
   row, and it costs no height here either. */
.build-checklist .build-title-state {
    flex: 0 0 auto;
    padding: 0.13rem 0.36rem;
    border: 1px solid rgba(224, 184, 95, 0.5);
    border-radius: 4px;
    background: rgba(224, 184, 95, 0.1);
    color: #e0b85f;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.05em;
    line-height: 1;
    text-transform: uppercase;
    white-space: nowrap;
    cursor: help;
}

/* An inline decision: saving is primary; leaving without saving is explicit. */
.build-checklist .build-new-prompt { margin-top: 10px; padding: 12px; border: 1px solid rgba(67,72,87,0.9); border-radius: 6px; background: #0d141e; }
.build-checklist .build-new-prompt__head { margin-bottom: 4px; }
.build-checklist .build-new-prompt__head strong { color: #e7ebf3; font-size: 13px; font-weight: 600; }
.build-checklist .build-new-prompt p { margin: 0 0 10px; color: #a4acbc; font-size: 12px; line-height: 1.4; }
.build-checklist .build-new-prompt__actions { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.build-checklist .build-new-prompt__actions button { width: 100%; min-height: 32px; margin: 0; padding: 5px 6px; font-size: 11px; font-weight: 600; }
.build-checklist .build-new-prompt__discard { border: 1px solid rgba(230,71,89,0.35); background: transparent; color: #edb0b8; }
.build-checklist .build-new-prompt__discard:hover:not(:disabled) { background: rgba(230,71,89,0.08); border-color: #e64759; }
.build-checklist .build-new-prompt__actions .build-new-prompt__cancel { grid-column: 1 / -1; min-height: 24px; padding: 2px; border: 0; border-radius: 5px; background: transparent; color: #a4acbc; cursor: pointer; }
.build-checklist .build-new-prompt__cancel:hover:not(:disabled) { color: #e7ebf3; }
.build-checklist .build-new-prompt button:focus-visible { outline: 2px solid #7fd0ee; outline-offset: 2px; }
.build-checklist .build-new-prompt button:disabled { opacity: 0.5; cursor: wait; }

/* Outcome of pressing Save. Replaces the old name-entry dropdown. */
.build-checklist .build-save-status {
    display: flex;
    align-items: flex-start;
    gap: 0.4rem;
    margin-top: 0.75rem;
    padding: 0.5rem 0.6rem;
    border: 1px solid rgba(67, 72, 87, 0.7);
    border-radius: 6px;
    background: rgba(8, 11, 17, 0.6);
    color: #9aa5b8;
    font-size: 11px;
    line-height: 1.4;
}

.build-checklist .build-save-status a {
    color: #77d8f5;
}

.build-checklist .build-save-status--saved {
    border-color: rgba(0, 188, 140, 0.45);
    background: rgba(0, 188, 140, 0.08);
    color: #9fe3cd;
}

.build-checklist .build-save-status--signin {
    border-color: rgba(32, 174, 222, 0.45);
    background: rgba(32, 174, 222, 0.08);
    color: #a8d8ea;
}

.build-checklist .build-save-status--error {
    border-color: rgba(230, 71, 89, 0.5);
    background: rgba(230, 71, 89, 0.08);
    color: #f0b6bc;
}

.build-checklist .sign-in-required {
    margin-top: 0.75rem;
    border-color: rgba(67, 72, 87, 0.78);
}

.build-actions {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.65rem;
    margin-top: 0.8rem;
}

.build-actions a {
    min-width: 0;
}

.build-save-control {
    display: grid;
    min-width: 0;
}

.build-save-control.has-copy-action {
    grid-template-columns: minmax(0, 1fr) 44px;
    gap: 4px;
}

.build-checklist .build-save-copy {
    min-width: 0;
    padding: 0 0.18rem;
    border-color: rgba(82, 111, 133, 0.72);
    color: #8fb8cc;
    font-size: 10px;
}

.build-checklist .build-affiliate-note {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 0.35rem;
    margin: 0.6rem 0 0;
    color: #98a3b5;
    font-size: 11px;
    line-height: 1.35;
    text-align: center;
}

.build-checklist .build-affiliate-note .icon {
    flex: 0 0 auto;
    margin-top: 0.1rem;
    color: #45c9f5;
    font-size: 10px;
}

.build-checklist .gc-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 38px;
    margin: 0;
    padding: 0 0.4rem;
    border: 1px solid rgba(25, 151, 198, 0.72);
    border-radius: 6px;
    background: rgba(25, 151, 198, 0.055);
    color: #1997c6;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.2px;
    line-height: 1;
    text-transform: uppercase;
    white-space: nowrap;
    transition: background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, color 160ms ease;
}

.build-checklist .gc-button:hover,
.build-checklist .gc-button:focus {
    border-color: #16b4ed;
    background: rgba(25, 151, 198, 0.16);
    box-shadow: 0 0 22px rgba(25, 151, 198, 0.12);
    color: #f4f8fb;
}

.build-checklist .gc-button.inactive,
.build-checklist .gc-button:disabled {
    border-color: rgba(67, 72, 87, 0.82);
    background: rgba(8, 10, 15, 0.28);
    color: #687084 !important;
    cursor: not-allowed;
    box-shadow: none;
}

.build-checklist .gc-button.inactive:hover,
.build-checklist .gc-button.inactive:focus,
.build-checklist .gc-button:disabled:hover,
.build-checklist .gc-button:disabled:focus {
    border-color: rgba(67, 72, 87, 0.82);
    background: rgba(8, 10, 15, 0.28);
    color: #687084 !important;
    box-shadow: none;
}

.build-checklist .drop-down-opened {
    border-top: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
}

@media screen and (min-width: 1201px) {
    .build-checklist {
        flex: 1 1 auto;
        height: auto !important;
        min-height: 0;
    }

    /* .build-checklist .build-metrics {
        margin: auto 0 1.1rem;
    } */
}

@media screen and (max-height: 820px) and (min-width: 1201px) {
    .build-checklist {
        max-height: calc(100vh - 78px);
        padding: 0 18px 8px 14px !important;
        overflow-y: auto;
        scrollbar-width: thin;
        scrollbar-color: rgba(145, 152, 170, 0.5) transparent;
    }

    .build-checklist::before {
        top: 35px;
        right: 20px;
        left: 14px;
    }

    .build-checklist .menu-label {
        min-height: 34px;
        margin-bottom: 0.42rem;
    }

    .build-checklist .menu-label h4 {
        font-size: 18px;
    }

    .build-checklist .menu-option {
        min-height: 38px;
        margin-bottom: 0.36rem;
        padding: 0 0.42rem;
    }

    .build-checklist .b-icon {
        width: 24px;
        min-width: 24px;
        font-size: 16px;
    }

    .build-checklist .item-label {
        font-size: 14px;
    }

    .build-summary {
        min-height: 32px;
        margin-top: 0.28rem;
    }

    .build-checklist .build-metrics {
        gap: 0.52rem;
        margin: 0.48rem 0 0.7rem;
    }

    .build-checklist .metric-head {
        margin-bottom: 0.28rem;
    }

    .build-checklist .metric-track {
        height: 5px;
    }

    .build-actions {
        gap: 0.45rem;
        margin-top: 0.55rem;
    }

    .build-checklist .gc-button {
        min-height: 32px;
        font-size: 12px;
    }
}

@media screen and (max-height: 720px) and (min-width: 1201px) {
    .build-checklist {
        max-height: calc(100vh - 58px);
        padding-right: 14px !important;
    }

    .build-checklist .menu-option {
        min-height: 34px;
        margin-bottom: 0.28rem;
    }

    .build-checklist .build-metrics {
        gap: 0.42rem;
        margin-bottom: 0.52rem;
    }
}

@media screen and (max-width: 1200px) {
    .build-checklist .item-label {
        width: auto !important;
        max-width: 142px;
    }

    .build-checklist .item-label.selected {
        max-width: min(142px, calc(100% - 28px));
    }
}
</style>
